import React, { forwardRef } from 'react'

import Button from '@bufferapp/ui/Button'
import { white, grayDark } from '@bufferapp/ui/style/colors'

import { appendPlaceholders } from '~/shared/utils'
import { LABELS, SERVICE_DEFAULTS } from '~/app/constants'
import { CTABackground, Label, Text } from '~/shared/components'
import Counter from '~/modules/post/components/Counter/Counter'
import PostThumbnail from '~/modules/post/components/PostThumbnail'
import { shortenText } from '~/shared/utils/strings'
import type { EngagePost, LabelCount } from '~/gql/graphql'

import {
  GridWrapper,
  PostWrapper,
  ContentWrapper,
  IndicatorsWrapper,
  CommentsWrapper,
  LabelsWrapper,
  FilterWrapper,
  FilterMessage,
  CommentCount,
  Placeholder,
  Overlay,
  TextWrapper,
  Row,
} from './styles'

export const COUNTER_LIMIT = 999
export const MAX_POST_TEXT_SIZE = 80

export type PostElement = {
  data: EngagePost
  fallbackImg: string
  placeholder?: boolean
}

type ServiceType = keyof typeof SERVICE_DEFAULTS

type GridProps = {
  loading?: boolean
  selected?: string
  posts?: PostElement[] | null
  filter?: {
    value: string
    count: number
  }
  service?: ServiceType
  handlers: {
    onSelect: (param: { data: EngagePost; fallbackImg: string }) => void
    onRefresh: (postId: string) => void
    onClearFilter: () => void
    onGoToPublish: () => void
  }
}

type GridElement = PostElement | { placeholder: boolean }

function splitInRows(
  elements: GridElement[],
  rowSize: number,
): GridElement[][] {
  const result = []
  const count = elements.length

  for (let i = 0; i < count; i += rowSize) {
    result.push(elements.slice(i, i + rowSize))
  }

  return result
}

const Grid = forwardRef<HTMLDivElement, GridProps>(
  (
    {
      loading = false,
      selected,
      posts = [],
      filter,
      service = 'instagram',
      handlers,
    },
    ref,
  ) => {
    const { onSelect, onRefresh, onClearFilter, onGoToPublish } = handlers

    const {
      postsPerRow,
      postsPerPage,
      imagesW,
      imagesH,
      label: serviceName,
    } = SERVICE_DEFAULTS[service as ServiceType]

    const postList = loading
      ? appendPlaceholders(posts as PostElement[], postsPerPage)
      : posts

    if (!filter && postList && postList.length === 0) {
      return <Empty serviceName={serviceName} onContinue={onGoToPublish} />
    }

    return (
      <GridWrapper
        id="post-grid"
        role="list"
        ref={ref}
        className={loading ? 'loading' : 'loaded'}
      >
        {filter && <FilterControls filter={filter} onClear={onClearFilter} />}
        {postList &&
          splitInRows(postList, postsPerRow).map((elements, rowIndex) => (
            <Row key={rowIndex}>
              {elements.map((element: GridElement, elIndex: number) =>
                'placeholder' in element ? (
                  <Placeholder
                    // @ts-expect-error TS(2769) FIXME: No overload matches this call.
                    width={imagesW}
                    height={imagesH}
                    key={`placeholder-${rowIndex}-${elIndex}`}
                    role="listitem"
                  />
                ) : (
                  <Post
                    key={`post-${element.data.id}`}
                    post={element.data}
                    fallbackImg={element.fallbackImg}
                    width={imagesW}
                    height={imagesH}
                    selected={selected === element.data.id}
                    onSelect={(): void => onSelect(element)}
                    onRefresh={(): void => onRefresh(element.data.id ?? '')}
                  >
                    {element.data.totalPendingComments != null &&
                      element.data.totalPendingComments > 0 && (
                        <Indicators
                          comments={element.data.totalPendingComments}
                          labelsCount={element.data.labelsCount}
                          isGiveaway={element.data.isGiveaway}
                        />
                      )}
                  </Post>
                ),
              )}
            </Row>
          ))}
      </GridWrapper>
    )
  },
)

Grid.displayName = 'Grid'

type PostProps = {
  post: EngagePost
  fallbackImg: string
  width: number
  height: number
  selected: boolean
  children: React.ReactNode
  onSelect: () => void
  onRefresh: () => void
}

const Post = ({
  post,
  fallbackImg,
  width,
  height,
  selected,
  children,
  onSelect,
  onRefresh,
}: PostProps): JSX.Element => (
  <PostWrapper
    // @ts-expect-error TS(2769) FIXME: No overload matches this call.
    width={width}
    height={height}
    selected={selected}
    onClick={onSelect}
    role="listitem"
    className="post-item"
    data-testid={selected ? 'post-selected' : null}
    data-post-id={post.id}
  >
    {/* @ts-expect-error TS(2769) FIXME: No overload matches this call. */}
    <ContentWrapper width={width} height={height} selected={selected}>
      <PostThumbnail
        src={post.mediaUrl || fallbackImg}
        selected={selected}
        onError={onRefresh}
      />
      {!post.mediaUrl && post.text && (
        <TextWrapper>
          {/* @ts-expect-error TS(2741) FIXME: Property 'className' is missing in type '{ childre... Remove this comment to see the full error message */}
          <Text size={14} color={white}>
            {shortenText(post.text, MAX_POST_TEXT_SIZE)}
          </Text>
        </TextWrapper>
      )}
      {!selected && <Overlay />}
      {children}
    </ContentWrapper>
  </PostWrapper>
)

type IndicatorsProps = {
  comments: number
  labelsCount?: Array<LabelCount | null> | null
  isGiveaway?: boolean | null
}

const Indicators = ({
  comments,
  labelsCount,
  isGiveaway,
}: IndicatorsProps): JSX.Element => {
  const count = comments > COUNTER_LIMIT ? '1K+' : comments

  const labels: JSX.Element[] = []
  labelsCount?.forEach((labelCount: LabelCount | null) => {
    if (labelCount?.count && labelCount.count > 0) {
      labels.push(<Label type={labelCount.label} key={labelCount.label} />)
    }
  })

  return (
    <IndicatorsWrapper data-testid="post-indicators">
      {/* @ts-expect-error TS(2769) FIXME: No overload matches this call. */}
      <CommentsWrapper standalone={!labels.length}>
        <Counter
          type={isGiveaway ? 'giveaway' : 'unanswered-comments'}
          value={count}
          colors={{ icon: white, value: white }}
        />
      </CommentsWrapper>
      <LabelsWrapper data-testid="post-labels">{labels}</LabelsWrapper>
    </IndicatorsWrapper>
  )
}

type FilterProps = {
  filter: {
    value: string
    count: number
  }
  onClear: () => void
}

const FilterControls = ({ filter, onClear }: FilterProps): JSX.Element => {
  const { value, count } = filter

  const { filterStr } = LABELS.find((el) => el.id === value) ?? {
    filterStr: '',
  }
  const message = `Unanswered ${filterStr} comments`

  return (
    <FilterWrapper data-testid="filter-control">
      <FilterMessage>
        <Label type={value} />
        {/* @ts-expect-error TS(2741) FIXME: Property 'className' is missing in type '{ childre... Remove this comment to see the full error message */}
        <Text size={14} weight={500} color={grayDark}>
          <CommentCount>{count}</CommentCount>
          {message}
        </Text>
      </FilterMessage>
      <Button type="secondary" onClick={onClear} label="Clear Filter" />
    </FilterWrapper>
  )
}

const Empty = ({
  serviceName,
  onContinue,
}: {
  serviceName: string
  onContinue: () => void
}): JSX.Element => (
  <CTABackground
    title={`There are no posts on this ${serviceName} account yet`}
    continueLabel="Create A New Post On Publish"
    onContinue={onContinue}
  />
)

export default Grid
